import React from "react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const Section: React.FC<{ icon: string; color: string; title: string; children: React.ReactNode }> = ({
  icon, color, title, children,
}) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      <span style={{
        width: 32, height: 32, borderRadius: 8, background: color + "1a",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <i className={icon} style={{ color, fontSize: 15 }} />
      </span>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1e293b" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 12, paddingLeft: 12, borderLeft: "2px solid #e2e8f0" }}>
    <div style={{ fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>{children}</div>
  </div>
);

const Tag: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = "#6366f1" }) => (
  <span style={{
    display: "inline-block", padding: "1px 8px", borderRadius: 4,
    background: color + "18", color, fontWeight: 600, fontSize: 12, marginRight: 4,
  }}>
    {children}
  </span>
);

const ApprovalPolicyGuide: React.FC<Props> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "rgba(15,23,42,0.45)", display: "flex",
        alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff", borderRadius: 16, width: "100%", maxWidth: 720,
          maxHeight: "88vh", display: "flex", flexDirection: "column",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: "20px 24px 16px", borderBottom: "1px solid #e2e8f0",
          display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              width: 36, height: 36, borderRadius: 10, background: "#6366f11a",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="ti ti-book-2" style={{ color: "#6366f1", fontSize: 18 }} />
            </span>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#1e293b" }}>Approval Policy — Quick Guide</h2>
              <p style={{ margin: 0, fontSize: 12, color: "#94a3b8", marginTop: 1 }}>
                How each section works
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none", background: "#f1f5f9", borderRadius: 8, width: 32,
              height: 32, cursor: "pointer", fontSize: 16, color: "#64748b",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <i className="ti ti-x" />
          </button>
        </div>

        <div style={{ overflowY: "auto", padding: "24px 24px 28px", flex: 1 }}>

          <Section icon="ti ti-file-text" color="#6366f1" title="Policy">
            <Field label="Title">
              The name that identifies this policy. It appears in the policy list and is used when
              assigning a policy to a proposal workflow.
            </Field>
            <Field label="Active">
              When <Tag>ON</Tag> the policy is evaluated against proposals. When <Tag color="#dc2626">OFF</Tag> the
              policy is completely ignored regardless of matching rules.
            </Field>
            <Field label="Manager Approval Required">
              Controls how <em>Team Manager</em> approver stages behave.
              <ul style={{ margin: "6px 0 0 0", paddingLeft: 18, lineHeight: 1.8 }}>
                <li>
                  <Tag>ON</Tag> — the team manager assigned to the proposal's team <strong>must explicitly approve</strong> every stage
                  that lists Team Manager as the approver. The proposal stays pending until that action is taken.
                </li>
                <li>
                  <Tag color="#10b981">OFF</Tag> — if a stage has Team Manager as the approver and the manager has not yet
                  acted, that stage is <strong>automatically approved</strong> and the workflow advances to the next stage.
                </li>
              </ul>
            </Field>
            <Field label="Priority Order">
              When more than one active policy matches a proposal, <strong>the policy with the lowest number is applied first</strong>.
              For example, a policy with priority <Tag>1</Tag> is evaluated before one with priority <Tag>5</Tag>.
              Use this to control which policy takes precedence when multiple policies are applicable.
            </Field>
          </Section>

          <div style={{ height: 1, background: "#f1f5f9", marginBottom: 24 }} />

          <Section icon="fa fa-filter" color="#8b5cf6" title="Rules">
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 14px 0", lineHeight: 1.7 }}>
              Rules are conditions that a proposal must satisfy for this policy to be triggered.
              Only when all rule groups evaluate to <Tag color="#10b981">true</Tag> will the policy's
              approval stages be activated.
            </p>
            <Field label="Field">
              The proposal attribute to evaluate — for example <Tag color="#475569">total_amount</Tag> or{" "}
              <Tag color="#475569">currency_code</Tag>. Changing the field resets the operator and value
              because each field supports a specific set of operators.
            </Field>
            <Field label="Operator">
              How the field value is compared.{" "}
              <Tag>Equal</Tag> exact match · <Tag>Greater Than</Tag> numeric threshold ·{" "}
              <Tag>Less Than</Tag> numeric ceiling · <Tag>Between</Tag> inclusive range (Min and Max inputs appear).
            </Field>
            <Field label="Value — dynamic by data type">
              The value input adapts automatically when you choose a field:
              <ul style={{ margin: "6px 0 0 0", paddingLeft: 18, lineHeight: 1.9 }}>
                <li><Tag color="#475569">integer</Tag> fields → number input, whole numbers only.</li>
                <li><Tag color="#475569">float</Tag> fields → number input, decimals allowed.</li>
                <li><Tag color="#475569">currency_code</Tag> field → currency dropdown (e.g. USD, EUR).</li>
                <li><Tag color="#475569">boolean</Tag> fields (names start with <code>is_</code>) → True / False dropdown.</li>
                <li><Tag color="#475569">date / datetime</Tag> fields → date-time picker.</li>
                <li><Tag color="#475569">string</Tag> fields → free-text input.</li>
              </ul>
            </Field>
            <Field label="AND / OR connectors">
              The connector shown between two rules groups them together.
              <ul style={{ margin: "6px 0 0 0", paddingLeft: 18, lineHeight: 1.8 }}>
                <li><Tag>AND</Tag> — both rules must be true at the same time.</li>
                <li><Tag color="#f59e0b">OR</Tag> — either rule being true is sufficient.</li>
              </ul>
              Rules without a connector are independent — each group is evaluated separately, and the
              policy triggers if <strong>any</strong> group matches.
            </Field>
          </Section>

          <div style={{ height: 1, background: "#f1f5f9", marginBottom: 24 }} />

          <Section icon="ti ti-git-merge" color="#10b981" title="Stages">
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 14px 0", lineHeight: 1.7 }}>
              Stages define the sequential approval steps. Each stage must be completed before the
              next one starts. Stages execute in the order you set.
            </p>
            <Field label="Stage Name">
              A descriptive label for this step, e.g. <em>Manager Review</em> or <em>Finance Sign-off</em>.
            </Field>
            <Field label="Order">
              The numeric position of this stage in the workflow. Stage <Tag>1</Tag> runs first,
              then <Tag>2</Tag>, and so on. If two stages share the same order number they may run in parallel.
            </Field>
            <Field label="Condition">
              Determines when this stage is required — for example <em>Always</em>, or only when
              the proposal exceeds a certain threshold. Available conditions come from your system configuration.
            </Field>
            <Field label="Approver — types and extra fields">
              Selecting an approver type may reveal an additional input:
              <ul style={{ margin: "6px 0 0 0", paddingLeft: 18, lineHeight: 2 }}>
                <li>
                  <Tag color="#10b981">Team Manager</Tag> — no extra input. The manager of the team
                  assigned to the proposal is the approver automatically.
                </li>
                <li>
                  <Tag color="#10b981">Assigned User</Tag> — no extra input. The user assigned to the
                  proposal is the approver automatically.
                </li>
                <li>
                  <Tag color="#6366f1">Sales User</Tag> — a multi-select dropdown appears listing all
                  active Sales Users in your tenant. One or more can be selected.
                </li>
                <li>
                  <Tag color="#6366f1">Client Admin</Tag> — a multi-select dropdown appears listing all
                  Client Admin users in your tenant. One or more can be selected.
                </li>
                <li>
                  <Tag color="#f59e0b">External Approver</Tag> — an email chip input appears. Type an
                  email address and press <kbd style={{ background: "#10b981", padding: "1px 5px", borderRadius: 3, fontSize: 11 }}>Enter</kbd>{" "}
                  or <kbd style={{ background: "#10b981", padding: "1px 5px", borderRadius: 3, fontSize: 11 }}>,</kbd> to add it.
                  Multiple external emails can be added.
                </li>
              </ul>
            </Field>
          </Section>

        </div>
      </div>
    </div>
  );
};

export default ApprovalPolicyGuide;
